/**
 * Preference Service
 * 
 * Service for managing user account preferences using Supabase client
 * Following the service pattern from profile-service.ts
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import type {
  UserPreferences,
  UpdatePreferencesInput,
  PreferenceResponse,
  DashboardPreferences
} from '../types/preference.types';
import { getDefaultPreferences } from '../lib/regions/au/preferences';

// Helper function to convert database row to UserPreferences
function convertDbRowToPreferences(row: any): UserPreferences {
  // Parse dashboard_preferences JSONB
  let dashboardPreferences: DashboardPreferences = {
    showSpendingChart: true,
    showRecentTransactions: true,
    showBudgetOverview: true,
  };

  if (row.dashboard_preferences && typeof row.dashboard_preferences === 'object') {
    dashboardPreferences = {
      showSpendingChart: row.dashboard_preferences.showSpendingChart ?? true,
      showRecentTransactions: row.dashboard_preferences.showRecentTransactions ?? true,
      showBudgetOverview: row.dashboard_preferences.showBudgetOverview ?? true,
    };
  }

  return {
    id: row.id,
    userId: row.user_id,
    language: row.language || 'en',
    currency: row.currency || 'AUD',
    currencyDisplay: row.currency_display || 'symbol',
    dateFormat: (row.date_format && row.date_format.trim()) || 'DD/MM/YYYY',
    timeFormat: row.time_format || '24h',
    timezone: row.timezone || 'Australia/Sydney',
    financialYearStart: row.financial_year_start || 'July',
    theme: row.theme || 'light',
    dashboardPreferences,
    transactionRetentionYears: row.transaction_retention_years ?? null,
    payFrequency: row.pay_frequency ?? null,
    payDay: row.pay_day ?? null,
    payAnchorDate: row.pay_anchor_date ?? null,
    estimatedNetPay: row.estimated_net_pay != null ? Number(row.estimated_net_pay) : null,
    payTransactionName: row.pay_transaction_name ?? null,
    gstRegistered: Boolean(row.gst_registered),
    businessName: row.business_name ?? null,
    businessAbn: row.business_abn ?? null,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
  };
}

// Helper function to convert UpdatePreferencesInput to database format
function convertUpdateInputToDb(input: UpdatePreferencesInput): any {
  const dbInput: any = {};
  
  if (input.language !== undefined) {
    dbInput.language = input.language;
  }
  if (input.currency !== undefined) {
    dbInput.currency = input.currency;
  }
  if (input.currencyDisplay !== undefined) {
    dbInput.currency_display = input.currencyDisplay;
  }
  if (input.dateFormat !== undefined) {
    // Preserve the exact value including spaces (for formats like 'DDD MMM DD YYYY')
    dbInput.date_format = input.dateFormat.trim();
  }
  if (input.timeFormat !== undefined) {
    dbInput.time_format = input.timeFormat;
  }
  if (input.timezone !== undefined) {
    dbInput.timezone = input.timezone;
  }
  if (input.financialYearStart !== undefined) {
    dbInput.financial_year_start = input.financialYearStart;
  }
  if (input.theme !== undefined) {
    dbInput.theme = input.theme;
  }
  if (input.dashboardPreferences !== undefined) {
    dbInput.dashboard_preferences = input.dashboardPreferences;
  }
  if (input.transactionRetentionYears !== undefined) {
    dbInput.transaction_retention_years = input.transactionRetentionYears;
  }
  if (input.payFrequency !== undefined) {
    dbInput.pay_frequency = input.payFrequency;
  }
  if (input.payDay !== undefined) {
    dbInput.pay_day = input.payDay;
  }
  if (input.payAnchorDate !== undefined) {
    dbInput.pay_anchor_date = input.payAnchorDate;
  }
  if (input.estimatedNetPay !== undefined) {
    dbInput.estimated_net_pay = input.estimatedNetPay;
  }
  if (input.payTransactionName !== undefined) {
    dbInput.pay_transaction_name =
      input.payTransactionName === null
        ? null
        : input.payTransactionName.trim() || null;
  }
  if (input.gstRegistered !== undefined) {
    dbInput.gst_registered = input.gstRegistered;
  }
  if (input.businessName !== undefined) {
    dbInput.business_name = input.businessName?.trim() || null;
  }
  if (input.businessAbn !== undefined) {
    dbInput.business_abn = input.businessAbn?.replace(/\s/g, '') || null;
  }

  dbInput.updated_at = new Date().toISOString();
  
  return dbInput;
}

export const preferenceService = {
  /**
   * Get current user's preferences
   * Creates default preferences if none exist
   */
  async getPreferences(): Promise<PreferenceResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Get preferences from user_preferences table
      const { data: preferencesData, error: preferencesError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (preferencesError) {
        if (preferencesError.code === 'PGRST116') {
          // Preferences not found - create default preferences
          const defaults = getDefaultPreferences();
          const insertData = {
            user_id: userId,
            ...convertUpdateInputToDb(defaults),
            created_at: new Date().toISOString(),
          };

          const { data: newPreferences, error: insertError } = await supabase
            .from('user_preferences')
            .insert(insertData)
            .select()
            .single();

          if (insertError) {
            return {
              error: {
                code: 'INSERT_ERROR',
                message: insertError.message || 'Failed to create default preferences'
              }
            };
          }

          return {
            data: convertDbRowToPreferences(newPreferences)
          };
        }
        
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: preferencesError.message || 'Failed to fetch preferences'
          }
        };
      }

      const preferences = convertDbRowToPreferences(preferencesData);

      return {
        data: preferences
      };
    } catch (error: any) {
      console.error('Error getting preferences:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to get preferences'
        }
      };
    }
  },

  /**
   * Update current user's preferences
   */
  async updatePreferences(input: UpdatePreferencesInput): Promise<PreferenceResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Validate input
      if (input.currency && !['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'].includes(input.currency)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid currency value'
          }
        };
      }

      if (input.dateFormat && !['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'DDD MMM DD YYYY'].includes(input.dateFormat)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid date format value'
          }
        };
      }

      if (input.financialYearStart && !['January', 'April', 'July', 'October'].includes(input.financialYearStart)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid financial year start value'
          }
        };
      }

      if (input.theme && !['light', 'dark', 'auto'].includes(input.theme)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid theme value'
          }
        };
      }

      if (input.transactionRetentionYears !== undefined && 
          input.transactionRetentionYears !== null && 
          ![1, 2, 5, 10].includes(input.transactionRetentionYears)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid transaction retention years value'
          }
        };
      }

      if (input.payFrequency !== undefined && input.payFrequency !== null) {
        if (!['weekly', 'fortnightly', 'monthly'].includes(input.payFrequency)) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Invalid pay frequency value'
            }
          };
        }

        if (input.payDay === undefined || input.payDay === null) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Pay day is required when pay frequency is set'
            }
          };
        }

        if (input.payFrequency === 'monthly') {
          if (!Number.isInteger(input.payDay) || input.payDay < 1 || input.payDay > 31) {
            return {
              error: {
                code: 'VALIDATION_ERROR',
                message: 'Invalid pay day value for monthly frequency'
              }
            };
          }
        } else if (!Number.isInteger(input.payDay) || input.payDay < 1 || input.payDay > 7) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Invalid pay day value for weekly/fortnightly frequency'
            }
          };
        }

        if (input.payFrequency === 'fortnightly' && !input.payAnchorDate) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Pay anchor date is required for fortnightly frequency'
            }
          };
        }
      }

      if (input.estimatedNetPay !== undefined &&
          input.estimatedNetPay !== null &&
          (typeof input.estimatedNetPay !== 'number' || Number.isNaN(input.estimatedNetPay) || input.estimatedNetPay <= 0)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid estimated net pay value'
          }
        };
      }

      const dbInput = convertUpdateInputToDb(input);

      // Update preferences
      const { data: updatedPreferences, error: updateError } = await supabase
        .from('user_preferences')
        .update(dbInput)
        .eq('user_id', userId)
        .select()
        .single();

      if (updateError) {
        // Log the error for debugging
        console.error('Update preferences error:', updateError);
        console.error('Attempted to save dateFormat:', dbInput.date_format);
        
        if (updateError.code === 'PGRST116') {
          // Preferences don't exist - create them with defaults + updates
          const defaults = getDefaultPreferences();
          const insertData = {
            user_id: userId,
            ...convertUpdateInputToDb({ ...defaults, ...input }),
            created_at: new Date().toISOString(),
          };

          const { data: newPreferences, error: insertError } = await supabase
            .from('user_preferences')
            .insert(insertData)
            .select()
            .single();

          if (insertError) {
            return {
              error: {
                code: 'INSERT_ERROR',
                message: insertError.message || 'Failed to create preferences'
              }
            };
          }

          return {
            data: convertDbRowToPreferences(newPreferences)
          };
        }

        return {
          error: {
            code: 'UPDATE_ERROR',
            message: updateError.message || 'Failed to update preferences'
          }
        };
      }

      return {
        data: convertDbRowToPreferences(updatedPreferences)
      };
    } catch (error: any) {
      console.error('Error updating preferences:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to update preferences'
        }
      };
    }
  },
};

